import {
  ComponentType,
  startTransition,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, useWindowDimensions, View } from 'react-native'
import { router } from 'expo-router'
import { trpc } from 'api'
import { FlashList } from '@shopify/flash-list'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import { useCustomer } from 'hooks/useSession'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import { useMaxWindowDimensions } from '@ui/theme/useRWD'
import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import { PrescriptionResponse } from '@ca/common'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import { useSelectPrescription } from '@order/hooks/useSelectPrescription'
import cn from '@ui/utils/cn'
import PrescriptionItem from './PrescriptionItem'
import { usePrescription } from '@order/hooks/usePrescription'
import { buttonsFixed, gap } from '@ui/theme/values'

export type PrescriptionButtonsProps = {
  prescriptionLocal: PrescriptionResponse | null
  onClose: () => void
  onConfirm: () => void
}

interface Props {
  open: boolean
  close: () => void
  onSelect?: (prescription: PrescriptionResponse) => void
  Buttons?: ComponentType<PrescriptionButtonsProps>
}

const PAGE_SIZE = 4

export default function PrescriptionsModal({
  open,
  close,
  onSelect,
  Buttons,
}: Props) {
  const { t } = useTranslation(['ui', 'modals', 'auth', 'common'])
  const dimensions = useWindowDimensions()
  const dimentionsMax = useMaxWindowDimensions()
  const customer = useCustomer()

  const { selectedId: selectedPrescriptionId, select: selectPrescription } =
    useSelectPrescription()

  const selectedPrescription = usePrescription()
  const utils = trpc.useUtils()

  const [prescriptionLocal, setPrescriptionLocal] =
    useState<PrescriptionResponse | null>(selectedPrescription || null)

  const {
    data,
    hasNextPage: hasNextPageRemote,
    fetchNextPage,
    isFetchingNextPage,
    failureCount,
  } = trpc.customer.prescriptions.list.useInfiniteQuery(
    {
      limit: PAGE_SIZE,
      id: customer?.id || 0,
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },
      enabled: !!customer?.id,
    }
  )

  const prescriptions = useFlattenInfiniteData(data)
  const setPrescriptionData = utils.customer.prescriptions.byId.setData
  const getPrescriptionData = utils.customer.prescriptions.byId.getData

  const [localPages, setLocalPages] = useState(1)

  useEffect(() => {
    if (!customer?.id || !prescriptions.length) {
      return
    }
    prescriptions.forEach((item) => {
      const key = {
        customerId: customer.id,
        prescriptionId: item.id,
      }
      const existing = getPrescriptionData(key)
      if (!existing) {
        setPrescriptionData(key, item)
      }
    })
  }, [customer?.id, getPrescriptionData, prescriptions, setPrescriptionData])

  const fixedStyle = {
    width: dimentionsMax.width,
    left: (dimensions.width - dimentionsMax.width) / 2,
  }

  const onClose = () => {
    setPrescriptionLocal(null)
    setLocalPages(1)
    close()
  }

  const onAddPrescription = () => {
    onClose()
    router.navigate({
      pathname: '/vision/prescription/create',
      params: { order: '1' },
    })
  }

  const activePrescription = prescriptionLocal ?? selectedPrescription ?? null
  const onConfirmPrescription = () => {
    if (!activePrescription) {
      return
    }
    if (onSelect) {
      onSelect(activePrescription)
    } else {
      selectPrescription(activePrescription)
    }
    onClose()
  }

  const toRender = PAGE_SIZE * localPages

  const hasNextPageLocal = toRender < prescriptions.length
  const hasNextPage = hasNextPageLocal || hasNextPageRemote

  const nextPage = () => {
    if (isFetchingNextPage || !hasNextPage || failureCount > 3) {
      return
    }
    const next = localPages + 1
    setLocalPages(next)
    const items = PAGE_SIZE * next
    if (hasNextPageRemote && items > prescriptions?.length) {
      fetchNextPage()
    }
  }

  // Id to mark as selected in the list
  const activeId = activePrescription?.id ?? selectedPrescriptionId ?? null

  const isLocalDifferent = useMemo(() => {
    return (
      !!selectedPrescription &&
      prescriptionLocal?.id !== selectedPrescription.id
    )
  }, [prescriptionLocal, selectedPrescription])

  const dataToRender = prescriptions.slice(0, toRender)

  return (
    <>
      <ModalWide
        cardComponentProps={{
          justifyContent: 'flex-end',
        }}
        open={open}
        close={onClose}
        body={
          <>
            <View
              className="relative inset-0 flex w-full flex-col"
              style={{
                paddingBottom: Platform.select({
                  default: buttonsFixed.height - gap,
                  web: 0,
                }),
              }}
            >
              <FlashList
                data={dataToRender}
                className="w-full"
                extraData={activeId}
                renderItem={({ item, index }) => {
                  return (
                    <View
                      style={{
                        // Hacky fix for web invalid item width
                        maxWidth: Platform.select({
                          default: undefined,
                          web: dimentionsMax.width,
                        }),
                      }}
                      className={cn('w-full p-8')}
                    >
                      <PrescriptionItem
                        data={item}
                        onPress={() => setPrescriptionLocal(item)}
                        isSelected={item.id === activeId}
                      />
                    </View>
                  )
                }}
                onEndReached={hasNextPage ? nextPage : undefined}
                onEndReachedThreshold={0.5}
                ListFooterComponent={isFetchingNextPage ? LoadingFooter : null}
              />
            </View>
          </>
        }
        childrenOutside={
          <>
            <ButtonsFixed.Gradient styleGradient={fixedStyle} />
            {Buttons ? (
              <Buttons
                prescriptionLocal={activePrescription}
                onClose={onClose}
                onConfirm={onConfirmPrescription}
              />
            ) : (
              <ButtonsFixed.Buttons
                disableTransitions
                style={fixedStyle}
                buttons={[
                  {
                    variant: 'outlined',
                    label: t('ui:addPrescription'),
                    onPress: onAddPrescription,
                  },
                  {
                    variant: isLocalDifferent ? 'filled' : 'outlined',
                    label: t('ui:selectPrescription'),
                    iconEnd: 'arrow-narrow-right',
                    onPress: onConfirmPrescription,
                  },
                ]}
              />
            )}
          </>
        }
      />
    </>
  )
}
